/**
 * FlowHub - Supabase Client Configuration
 * Centralized Supabase client with TypeScript support
 */

import { createClient } from '@supabase/supabase-js';

// Environment variables (set these in .env.local)
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Please check your .env.local file.'
  );
}

/**
 * Supabase client instance
 * Use this throughout the application for all Supabase operations
 */
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

// Expose supabase to window for debugging (only in development)
if (import.meta.env.DEV) {
  (window as any).supabase = supabase;
  console.log('✅ Supabase client loaded and exposed to window.supabase');
  console.log('Supabase URL:', supabaseUrl);
}

/**
 * Auth helpers
 */
export const auth = {
  /**
   * Sign up a new user
   */
  signUp: async (email: string, password: string, metadata?: { role?: string; full_name?: string }) => {
    return await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
      },
    });
  },

  /**
   * Sign in with email and password
   */
  signIn: async (email: string, password: string) => {
    return await supabase.auth.signInWithPassword({
      email,
      password,
    });
  },

  /**
   * Sign out current user
   */
  signOut: async () => {
    return await supabase.auth.signOut();
  },

  /**
   * Get current session
   */
  getSession: async () => {
    return await supabase.auth.getSession();
  },

  /**
   * Get current user
   */
  getUser: async () => {
    return await supabase.auth.getUser();
  },

  /**
   * Subscribe to auth state changes
   */
  onAuthStateChange: (callback: (event: string, session: any) => void) => {
    return supabase.auth.onAuthStateChange(callback);
  },

  
  /**
   * MFA (Multi-Factor Authentication) helpers
   */
  mfa: {
    /**
     * Get authenticator assurance level
     */
    getAuthenticatorAssuranceLevel: async () => {
      return await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    },

    /**
     * List MFA factors for current user
     */
    listFactors: async () => {
      return await supabase.auth.mfa.listFactors();
    },

    /**
     * Create MFA challenge (step 1 of verification)
     */
    challenge: async (params: { factorId: string }) => {
      return await supabase.auth.mfa.challenge(params);
    },

    /**
     * Verify MFA code (step 2 of verification)
     */
    verify: async (params: { factorId: string; challengeId: string; code: string }) => {
      return await supabase.auth.mfa.verify(params);
    },

    /**
     * Challenge and verify MFA code (combined method)
     * @deprecated Use challenge() + verify() separately for better control
     */
    challengeAndVerify: async (params: { factorId: string; code: string }) => {
      return await supabase.auth.mfa.challengeAndVerify(params);
    },

    /**
     * Enroll in MFA
     */
    enroll: async (params: { factorType: 'totp'; friendlyName?: string }) => {
      return await supabase.auth.mfa.enroll(params);
    },

    /**
     * Unenroll from MFA
     */
    unenroll: async (params: { factorId: string }) => {
      return await supabase.auth.mfa.unenroll(params);
    },
  },
};

/**
 * Database helpers
 */
export const db = {
  // N8N Instances
  n8nInstances: {
    list: () => supabase.from('n8n_instances').select('*').order('created_at', { ascending: false }),
    getById: (id: string) => supabase.from('n8n_instances').select('*').eq('id', id).single(),
    create: (data: any) => supabase.from('n8n_instances').insert(data).select().single(),
    update: (id: string, data: any) => supabase.from('n8n_instances').update(data).eq('id', id).select().single(),
    delete: (id: string) => supabase.from('n8n_instances').delete().eq('id', id),
  },

  // Products
  products: {
    list: async () => {
      let { data, error } = await supabase
        .from('products')
        .select('*, n8n_instance:n8n_instances(*), product_form:product_forms(*)')
        .order('created_at', { ascending: false });
      if (error) {
        const isRelationshipError = (error?.code === 'PGRST200') || (error?.message && (error.message.includes('Could not find a relationship') || error.message.includes('Could not find table')));
        if (isRelationshipError) {
          const { data: fallback, error: fallbackError } = await supabase
            .from('products')
            .select('*, n8n_instance:n8n_instances(*)')
            .order('created_at', { ascending: false });
          return { data: fallback, error: fallbackError };
        }
        return { data, error };
      }
      return { data, error };
    },

    listEnabled: async () => {
      let { data, error } = await supabase
        .from('products')
        .select('*, n8n_instance:n8n_instances(*), product_form:product_forms(*)')
        .eq('is_enabled', true)
        .order('created_at', { ascending: false });
      if (error) {
        const isRelationshipError = (error?.code === 'PGRST200') || (error?.message && (error.message.includes('Could not find a relationship') || error.message.includes('Could not find table')));
        if (isRelationshipError) {
          const { data: fallback, error: fallbackError } = await supabase
            .from('products')
            .select('*, n8n_instance:n8n_instances(*)')
            .eq('is_enabled', true)
            .order('created_at', { ascending: false });
          return { data: fallback, error: fallbackError };
        }
        return { data, error };
      }
      return { data, error };
    },

    getById: async (id: string) => {
      let { data, error } = await supabase
        .from('products')
        .select('*, n8n_instance:n8n_instances(*), product_form:product_forms(*)')
        .eq('id', id)
        .single();
      if (error) {
        const isRelationshipError = (error?.code === 'PGRST200') || (error?.message && (error.message.includes('Could not find a relationship') || error.message.includes('Could not find table')));
        if (isRelationshipError) {
          const { data: fallback, error: fallbackError } = await supabase
            .from('products')
            .select('*, n8n_instance:n8n_instances(*)')
            .eq('id', id)
            .single();
          return { data: fallback, error: fallbackError };
        }
        return { data, error };
      }
      return { data, error };
    },
    create: (data: any) => supabase.from('products').insert(data).select().single(),
    update: (id: string, data: any) => supabase.from('products').update(data).eq('id', id).select().single(),
    delete: (id: string) => supabase.from('products').delete().eq('id', id),
  },

  // Client Products
  clientProducts: {
    list: async () => {
      let { data, error } = await supabase
        .from('client_products_with_subscription')
        .select('*, product:products(*, n8n_instance:n8n_instances(*))')
        .order('created_at', { ascending: false });
      if (error) {
        const isMissingViewError = (error?.code === 'PGRST205') || (error?.message && (error.message.includes("Could not find the table 'public.client_products_with_subscription'") || error.message.includes("Could not find table 'public.client_products_with_subscription'")));
        if (isMissingViewError) {
          console.warn('View client_products_with_subscription missing from schema cache; retrying from base client_products');
          const { data: fallback, error: fallbackError } = await supabase
            .from('client_products')
            .select('*, product:products(*, n8n_instance:n8n_instances(*))')
            .order('created_at', { ascending: false });
          return { data: fallback, error: fallbackError };
        }
        return { data, error };
      }
      return { data, error };
    },
    getById: async (id: string) => {
      let { data, error } = await supabase
        .from('client_products_with_subscription')
        .select('*, product:products(*, n8n_instance:n8n_instances(*))')
        .eq('id', id)
        .single();
      if (error) {
        const isMissingViewError = (error?.code === 'PGRST205') || (error?.message && (error.message.includes("Could not find the table 'public.client_products_with_subscription'") || error.message.includes("Could not find table 'public.client_products_with_subscription'")));
        if (isMissingViewError) {
          const { data: fallback, error: fallbackError } = await supabase
            .from('client_products')
            .select('*, product:products(*, n8n_instance:n8n_instances(*))')
            .eq('id', id)
            .single();
          return { data: fallback, error: fallbackError };
        }
        return { data, error };
      }
      return { data, error };
    },
    create: (data: any) => supabase.from('client_products').insert(data).select().single(),
    update: (id: string, data: any) => supabase.from('client_products').update(data).eq('id', id).select().single(),
    delete: (id: string) => supabase.from('client_products').delete().eq('id', id),
  },

  // OAuth Tokens
  oauthTokens: {
    list: () => supabase.from('oauth_tokens').select('*'),
    getByProvider: (clientProductId: string, provider: string) =>
      supabase.from('oauth_tokens').select('*').eq('client_product_id', clientProductId).eq('provider', provider).single(),
    create: (data: any) => supabase.from('oauth_tokens').insert(data).select().single(),
    update: (id: string, data: any) => supabase.from('oauth_tokens').update(data).eq('id', id).select().single(),
    delete: (id: string) => supabase.from('oauth_tokens').delete().eq('id', id),
  },

  // User Profiles
  userProfiles: {
    getById: (id: string) => supabase.from('user_profiles').select('id, full_name, company_name, job_title, phone, cpf_cnpj, postal_code, address, address_number, complement, city, state, is_suspended, role, created_at, updated_at').eq('id', id).maybeSingle(),
    getByIdSafe: async (id: string) => {
      // Use limit() instead of single() to avoid REST API 406 errors
      // Only select columns that exist in user_profiles table
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, full_name, company_name, role, created_at, updated_at')
        .eq('id', id)
        .limit(1);
      
      if (error) {
        console.error('Error fetching user profile:', error);
        return { data: null, error };
      }
      
      return { data: data && data.length > 0 ? data[0] : null, error: null };
    },
    create: (id: string, data: any) => supabase.from('user_profiles').insert({ id, ...data }).select().single(),
    update: (id: string, data: any) => supabase.from('user_profiles').update(data).eq('id', id).select().single(),
  },
};

/**
 * Edge Functions helpers
 */
export const functions = {
  /**
   * Invoke an edge function
   */
  invoke: async <T = any>(functionName: string, options?: { body?: any; headers?: Record<string, string> }): Promise<T> => {
    const { data, error } = await supabase.functions.invoke(functionName, options);

    if (error) {
      // Try to parse a more friendly error message from the function response body.
      let friendlyError: Error | null = null;
      try {
        // Construct the edge function URL and perform a direct fetch to get the JSON body.
        const url = `${supabaseUrl}/functions/v1/${functionName}`;

        // Include both api key, but only set Authorization when we have an access token.
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          apikey: supabaseAnonKey,
        };

        // If the current session exists, include access token so the function receives authentication context
        try {
          const { data: sessionData } = await supabase.auth.getSession();
          const accessToken = sessionData?.session?.access_token;
          if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
        } catch (sessionErr) {
          // ignore session retrieval errors
        }

        const resp = await fetch(url, {
          method: options?.body ? 'POST' : 'GET',
          headers: { ...headers, ...(options?.headers || {}) },
          body: options?.body ? JSON.stringify(options?.body) : undefined,
        });

        let parsed: any = null;
        try {
          parsed = await resp.json();
        } catch (jsonErr) {
          // Not JSON, ignore
        }

        if (parsed && (parsed.message || parsed.error)) {
          friendlyError = new Error(parsed.message || parsed.error || error.message);
        }
      } catch (fetchErr: any) {
        // If we couldn't parse a friendly message, log the underlying fetch error and continue to throw original supabase error
        console.warn('Could not extract friendly message from edge function response', fetchErr);
      }

      // If we extracted a friendly message, throw it; otherwise throw the original supabase error.
      if (friendlyError) throw friendlyError;
      throw error;
    }

    return data;
  },

  // Specific function wrappers
  listWorkflows: async (n8nInstanceId: string, tag?: string) => {
    return functions.invoke('list-workflows', { body: { n8n_instance_id: n8nInstanceId, tag } });
  },

  parseWorkflowSchema: async (n8nInstanceId: string, workflowId: string) => {
    return functions.invoke('parse-workflow-schema', {
      body: {
        n8n_instance_id: n8nInstanceId,
        workflow_id: workflowId,
      }
    });
  },

  cloneWorkflow: async (productId: string, clientProductId: string, config: Record<string, any>) => {
    return functions.invoke('clone-workflow', {
      body: {
        product_id: productId,
        client_product_id: clientProductId,
        config,
      }
    });
  },

  deleteWorkflow: async (clientProductId: string) => {
    return functions.invoke('delete-workflow', {
      body: {
        client_product_id: clientProductId,
      }
    });
  },

  toggleWorkflow: async (clientProductId: string, isActive: boolean) => {
    return functions.invoke('toggle-workflow', {
      body: {
        client_product_id: clientProductId,
        is_active: isActive,
      }
    });
  },

  updateWorkflowConfig: async (clientProductId: string, config: Record<string, any>) => {
    return functions.invoke('update-workflow-config', {
      body: {
        client_product_id: clientProductId,
        config,
      }
    });
  },

  getWorkflowConfig: async (clientProductId: string) => {
    return functions.invoke('get-workflow-config', {
      body: {
        client_product_id: clientProductId,
      }
    });
  },

  getProductConfig: async (clientProductId: string) => {
    return functions.invoke('get-product-config', {
      body: {
        client_product_id: clientProductId,
      }
    });
  },

  updateProductConfig: async (clientProductId: string, config: Record<string, any>) => {
    return functions.invoke('update-product-config', {
      body: {
        client_product_id: clientProductId,
        config,
      }
    });
  },

  getTemplateConfig: async (productId: string) => {
    return functions.invoke('get-template-config', {
      body: {
        product_id: productId,
      }
    });
  },

  updateTemplateConfig: async (productId: string, config: Record<string, any>) => {
    return functions.invoke('update-template-config', {
      body: {
        product_id: productId,
        config,
      }
    });
  },

  syncProductChanges: async (productId: string, name: string, description?: string) => {
    return functions.invoke('sync-product-changes', {
      body: {
        product_id: productId,
        name,
        description,
      }
    });
  },

  listClients: async () => {
    return functions.invoke('list-clients', {});
  },

  getWorkflowCredentials: async (clientProductId: string) => {
    return functions.invoke('get-workflow-credentials', {
      body: {
        client_product_id: clientProductId,
      }
    });
  },

  createOAuthCredential: async (params: {
    client_product_id: string;
    credential_type: string;
    credential_data: Record<string, any>;
  }) => {
    return functions.invoke('create-oauth-credential', { body: params });
  },

  deleteOAuthCredential: async (params: {
    client_product_id: string;
    credential_type: string;
  }) => {
    return functions.invoke('delete-oauth-credential', { body: params });
  },

  finalizeOAuth: async (params: {
    code: string;
    credential_id: string;
    client_product_id: string;
  }) => {
    return functions.invoke('finalize-oauth', { body: params });
  },

  createTelegramCredential: async (params: {
    client_product_id: string;
    access_token: string;
  }) => {
    return functions.invoke('create-telegram-credential', { body: params });
  },

  getTelegramWebhookInfo: async (params: {
    client_product_id: string;
  }) => {
    return functions.invoke('get-telegram-webhook-info', { body: params });
  },

  detectFormAI: async (params: {
    n8n_instance_id: string;
    workflow_id: string;
    workflow_name?: string;
  }) => {
    return functions.invoke('detect-form-ai', { body: params });
  },

  // createFormTable has been deprecated and removed. Table creation is no longer supported.

  registerProductForm: async (params: { product_id: string }) => {
    return functions.invoke('register-product-form', { body: params });
  },

  getDashboardData: async (params: { client_product_id: string }) => {
    return functions.invoke('get-dashboard-data', { body: params });
  },

  contractProduct: async (params: { product_id: string; config?: Record<string, any>; confirmed_recontract?: boolean }) => {
    return functions.invoke('contract-product', { body: params });
  },

  createUser: async (params: {
    email: string;
    password: string;
    full_name: string;
    role: 'client' | 'admin';
  }) => {
    return functions.invoke('create-user', { body: params });
  },

  adminCreateProduct: async (params: any) => {
    return functions.invoke('admin-create-product', { body: params });
  },

  getWorkflowStatus: async (params: {
    workflow_id: string;
    n8n_instance: any;
  }) => {
    return functions.invoke('get-workflow-status', { body: params });
  },

  createSetupPayment: async (params: { ticket_id: string }) => {
    return functions.invoke('create-setup-payment', { body: params });
  },
};

export default supabase;
